import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import './Post.css';
import { formatTimestamp, detectLink } from '../../../common/utils';
import iconLike from '../../../../assets/icons/icon_post_like.svg';
import iconLiked from '../../../../assets/icons/icon_post_liked.svg';
import iconReply from '../../../../assets/icons/icon_post_reply.svg';
import iconShare from '../../../../assets/icons/icon_post_share.svg';
import { likePost } from '../../../../api/playgroundServices';
import iconMore from '../../../../assets/icons/icon_post_more.svg';
import PostOptionModal from './PostOptionModal';
import { deletePost } from '../../../../api/playgroundServices';
import iconCollabApply from '../../../../assets/icons/icon_collab_apply.svg';
import CollabGuidelines from './CollabGuidelines';
import imgContentMission from '../../../../assets/img/img_content_mission.png';
import imgThingsToAvoid from '../../../../assets/img/img_things_to_avoid.png';
import imgProducts from '../../../../assets/img/img_products.png';
import imgRequiredTags from '../../../../assets/img/img_required_tags.png';
import PostSkeleton from './PostSkeleton';
import { PostMedia, PostProps } from '../../../../types/postType';
import VIModal from '../../../common/VIModal/VIModal';
import Avatar from '../../../common/Avatar/Avatar';
import { useNavigate } from 'react-router-dom';
import VIImage from '../../../common/VIImage/VIImage';
import VividMentionVector from '../../../../assets/icons/vivid-mention-vector.svg';
import InstagramMentionVector from '../../../../assets/icons/instagram-mention-vector.svg';
import TiktokMentionVector from '../../../../assets/icons/tiktok-mention-vector.svg';
import { getUsernameById } from '../../../../api/highlightServices';
import { getImageStyles } from '../../../../services/mediaServices';
import DOMPurify from 'dompurify';
import MediaViewerModal from './MediaViewerModal';
import ApplyCollabsStepsModal from './ApplyCollabsStepsModal';
import { verifyAccessToken } from '../../../../api/auth';
import { applyCollabs } from '../../../../api/collabsServices';
import { trackCollabApplicationView } from '../../../../utils/tiktokTracking';

interface MentionData {
  type: 'vivid' | 'instagram' | 'tiktok';
  start_pos: number;
  end_pos: number;
}

interface Match {
  index: number;
  match: string;
  type: 'mention' | 'hashtag';
  id: string;
  username?: string;
  highlightType?: 'user' | 'tiktok' | 'ig' |'hashtag';
}

interface ImageDimensions {
  width: number;
  height: number;
  aspectRatio: 'vertical' | 'horizontal' | 'square';
}

const Post: React.FC<PostProps> = ({ 
  id,
  user, 
  content, 
  numLikes, 
  numReplies, 
  timestamp, 
  postType, 
  mediaList,
  preview,
  likedStatus: initialLikedStatus,
  currentUser,
  currentUserId,
  onPostDeleted,
  onReplyClick,
  onClick,
  isInPostDetails = false,
  collab,
  isLoading,
  onApplyClick,
  slug,
  highlight_username_dict,
  collabs_application_status,
}) => {
  const navigate = useNavigate();

  const handleUserClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/profile/${user.username}`);
  };

  // Add debug logging for initial render


  const [likedStatus, setLikedStatus] = useState(initialLikedStatus);
  const [likeCount, setLikeCount] = useState(numLikes);
  const [postContent, setPostContent] = useState(content || '');
  // const [linkPreview, setLinkPreview] = useState<LinkPreviewData | null>(null);
  const divRef = useRef<HTMLDivElement>(null);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [guidelineType, setGuidelineType] = useState<'content_mission' | 'things_to_avoid' | 'products' | 'required_hashtags'>('content_mission');
  // const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const [shouldShowMore, setShouldShowMore] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const CHARACTER_LIMIT = 200;
  const [renderedContent, setRenderedContent] = useState<JSX.Element | null>(null);
  const [isMediaViewerOpen, setIsMediaViewerOpen] = useState(false);
  const [mediaViewerIndex, setMediaViewerIndex] = useState(0);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showEditApplicationModal, setShowEditApplicationModal] = useState(false);

  useEffect(() => {
    const loadContent = async () => {
      if (content) {
        const rendered = await renderContent(content);
        setRenderedContent(rendered);
      }
    };

    loadContent();
  }, [content]);

  const contentRef = useRef<HTMLDivElement>(null);
  const seeMoreRef = useRef<HTMLSpanElement>(null);

  const handleSeeMoreClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (contentRef.current) {
      contentRef.current.style.maxHeight = 'none';
      contentRef.current.style.webkitLineClamp = 'unset';
      contentRef.current.style.display = 'block';
    }
    if (seeMoreRef.current) {
      seeMoreRef.current.style.display = 'none';
    }
  };

  const handleGuidelineClick = (type: 'content_mission' | 'things_to_avoid' | 'products' | 'required_hashtags') => {
    setGuidelineType(type);
    setShowGuidelines(true);
  };





  const handleLike = () => {
    if (!currentUser) {
      // Optionally handle unauthenticated like attempts
      return;
    }
    setLikedStatus(!likedStatus);
    setLikeCount(prevCount => likedStatus ? prevCount - 1 : prevCount + 1);
    
    likePost(id.toString()).catch(error => {
      console.error('Error liking post:', error);
    });
  };

  const handleMoreClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowOptionsModal(true);
  }, [id]);

  const handleCloseModal = useCallback(() => {
    setShowOptionsModal(false);
  }, [id]);

  const handleCopyLink = useCallback(() => {
    // Implement copy link functionality
    setShowOptionsModal(false);
  }, [id]);




  
  const handleDeletePost = async () => {
    try {
      setShowOptionsModal(false);
      
      // Wait for the API call to complete
      const success = await deletePost(id);
      
      if (success) {
        console.log('Post deleted successfully on the server for post:', id);
        // Only update UI after successful deletion
        onPostDeleted(id);
      } else {
        console.error('Failed to delete post:', id);
        // Optionally show an error message to the user
        // setError('Failed to delete post. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting post:', id, error);
      // Optionally show an error message to the user
      // setError('An error occurred while deleting the post.');
    }
  };

  const handleReplyClick = useCallback(() => {
    onReplyClick();
  }, [onReplyClick]);

  const handleCloseReplyModal = useCallback(() => {
    setShowReplyModal(false);
  }, []);

  const handleCancel = () => {
    setShowReplyModal(false);
    console.log(showReplyModal);
  };

  const handleSubmit = (replyContent: string) => {
    // Logic to submit the reply
    // Close the modal after submission
    handleCancel();
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowShareModal(true);
  };

  const handleCopyUrl = async () => {
    const url = `${window.location.origin}/posts/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const handleMediaContainerClick = (e: React.MouseEvent, index: number) => {
    console.log('🔍 MEDIA CONTAINER CLICKED:', {
      index,
      target: e.target,
      currentTarget: e.currentTarget,
      eventType: e.type,
      mediaItem: mediaList && mediaList[index],
    });
    
    e.preventDefault();
    e.stopPropagation();
    
    // Check if the click is on a button
    const isButtonClick = (e.target as HTMLElement).closest('button');
    console.log('Is button click?', isButtonClick);
    
    if (isButtonClick) {
      console.log('🛑 Button click detected - not opening media viewer');
      return;
    }
    
    console.log('✅ Setting media viewer to open with index:', index);
    setMediaViewerIndex(index);
    setIsMediaViewerOpen(true);
    
    // Add a timeout to check if the state was actually updated
    setTimeout(() => {
      console.log('⏱️ State after timeout:', { 
        isMediaViewerOpen, 
        mediaViewerIndex,
        mediaList
      });
    }, 100);
  };

  const handleJoinNowClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Join Now clicked:', {
      collabs_application_status,
      currentUser,
      collab
    });

    // First check if user is logged in
    if (!currentUser) {
      // Navigate to sign in
      navigate('/signin');
      return;
    }
    
    // Track TikTok events for collab application view
    await trackCollabApplicationView(
      String(id),
      content || 'Collaboration Application',
      currentUser
    );
    
    // Check if user has already applied - collabs_application_status will be a string like "pending" or "approved"
    if (collabs_application_status && collabs_application_status !== '') {
      console.log('User has already applied with status:', collabs_application_status);
      setShowEditApplicationModal(true);
    } else {
      console.log('First time applying, showing apply modal');
      setShowApplyModal(true);
    }
  };

  const handleEditConfirm = () => {
    // User confirmed they want to edit their application
    setShowEditApplicationModal(false);
    setShowApplyModal(true);
  };

  const handleApplySubmit = async (text: string, images: File[], gif: string | null): Promise<void> => {
    if (!collab) return;
    
    try {
      // Submit to server
      await applyCollabs(collab.id.toString(), text, images);
      setShowApplyModal(false);
    } catch (error) {
      console.error('Error applying to collab:', error);
      throw error; // Re-throw to let modal handle error state
    }
  };

  const renderContent = (content: string) => {
    console.log('Rendering content:', content);
    console.log('highlight_username_dict:', highlight_username_dict);

    const mentionRegex = /\{(user|tiktok|ig):([^}]+)\}/g;
    const hashtagRegex = /#\{tag:([^}]+)\}/g;
    let lastIndex = 0;
    const elements: JSX.Element[] = [];
    let text = content;
    let matches: Match[] = [];

    // Collect all mentions
    let mentionMatch;
    while ((mentionMatch = mentionRegex.exec(text)) !== null) {
      const mentionType = mentionMatch[1];  // 'user', 'tiktok', or 'ig'
      const mentionValue = mentionMatch[2]; // userId or username
      const [id, highlight_text] = mentionValue.split(':');
      
      console.log('Found mention:', {
        mentionType,
        mentionValue,
        id,
        highlight_text,
        dictUsername: highlight_username_dict?.[id],
        fullMatch: mentionMatch[0]
      });

      matches.push({
        index: mentionMatch.index,
        match: mentionMatch[0],
        type: 'mention',
        id: id,
        highlightType: mentionType as 'user' | 'tiktok' | 'ig' | 'hashtag',
        username: mentionType === 'user' 
          ? highlight_username_dict?.[id] 
          : highlight_text || id
      });
    }

    // Collect all hashtags
    let hashtagMatch;
    while ((hashtagMatch = hashtagRegex.exec(text)) !== null) {
      matches.push({
        index: hashtagMatch.index,
        match: hashtagMatch[0],
        type: 'hashtag',
        id: hashtagMatch[1],
        highlightType: 'hashtag',
        username: hashtagMatch[1] // Store the hashtag text in username field
      });
    }
    console.log('All collected matches:', matches);
    // Sort matches by index to maintain order
    matches.sort((a, b) => a.index - b.index);

    // Process all matches in order
    matches.forEach(match => {
      if (match.index > lastIndex) {
        elements.push(
          <span key={`text-${elements.length}`}>
            {text.slice(lastIndex, match.index)}
          </span>
        );
      }

      console.log('Processing match:', {
        type: match.type,
        username: match.username,
        id: match.id,
        highlightType: match.highlightType
      });

      if (match.type === 'mention') {
        let mentionHref = '';
        let mentionIconSrc;
        let mentionIconStyle: React.CSSProperties = {
          width: '14px',
          height: '14px',
          marginRight: '0px',
          verticalAlign: 'text-bottom'
        };

        switch (match.highlightType) {
          case 'ig':
            mentionHref = `https://instagram.com/${match.username}`;
            mentionIconSrc = InstagramMentionVector;
            break;
          case 'tiktok':
            mentionHref = `https://tiktok.com/@${match.username}`;
            mentionIconSrc = TiktokMentionVector;
            break;
          default: // 'user'
            mentionHref = `/profile/${match.username}`;
            mentionIconSrc = VividMentionVector;
            break;
        }

        elements.push(
          <span
            key={`mention-${elements.length}`}
            className="mention"
            onClick={(e) => {
              e.stopPropagation();
              if (match.highlightType === 'user') {
                navigate(mentionHref);
              } else {
                window.open(mentionHref, '_blank', 'noopener,noreferrer');
              }
            }}
            style={{ 
              color: '#0095F6', 
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center'
            }}
          >
            <img 
              src={mentionIconSrc} 
              alt={`${match.highlightType} mention`} 
              style={mentionIconStyle}
            />
            <span style={{ marginLeft: '1px', color: 'inherit' }}>
              {match.highlightType === 'user' 
                ? highlight_username_dict?.[match.id] || match.id
                : match.username}
            </span>
          </span>
        );
      } else if (match.type === 'hashtag') {
        elements.push(
          <span
            key={`hashtag-${elements.length}`}
            className="hashtag"
            onClick={(e) => {
              e.stopPropagation();
              navigate('/search', {
                state: {
                  searchState: 'results',
                  searchQuery: match.username,
                  fromHashtag: true
                }
              });
            }}
            style={{ 
              color: '#0095F6', 
              cursor: 'pointer',
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center'
            }}
          >
            <span style={{ color: 'inherit' }}>
              #{match.username}
            </span>
          </span>
        );
      }

      lastIndex = match.index + match.match.length;
    });

    // Add remaining text
    if (lastIndex < text.length) {
      elements.push(
        <span key={`text-${elements.length}`}>
          {text.slice(lastIndex)}
        </span>
      );
    }

    const contentElement = <div className="post-text-content">{elements}</div>;

    console.log('content', content);
    console.log('content.length', content.length);
    if (content.length > 300 && !isExpanded) {

      return (
        <div style={{ position: 'relative' }}>
          <div 
            ref={contentRef}
            style={{ 
              maxHeight: '4.5em',
              overflow: 'hidden',
              WebkitLineClamp: '3',
              WebkitBoxOrient: 'vertical',
              display: '-webkit-box',
              textOverflow: 'ellipsis',
              wordBreak: 'break-word'
            }}
          >
            {contentElement}
          </div>
          <span 
            ref={seeMoreRef}
            style={{ 
              color: '#0095F6', 
              cursor: 'pointer',
              marginLeft: '4px',
              display: 'inline-block'
            }}
            onClick={handleSeeMoreClick}
          >
            See more...
          </span>
        </div>
      );
    }

    return contentElement;
  };
  

  const isOwnPost = currentUser ? user.id.toString() === currentUser.id.toString() : false;


  // Add debug logging

  const handleContentClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on media elements
    const target = e.target as HTMLElement;
    const isMediaElement = target.closest('.post-mediaList') || 
                          target.closest('.link-preview') ||
                          target.closest('.collab-buttons');
    
    if (!isMediaElement && onClick) {
      onClick();
    }
  };

  const handleApplyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Navigate to post details if we're not already there
    if (!isInPostDetails && onClick) {
      onClick(); // This will trigger navigation to post details
      return;
    }
    
    // If we're already in post details, handle the apply action
    if (onApplyClick) {
      onApplyClick();
    }
  };

  const formatContent = (content: string) => {
    // First sanitize the content for security
    const sanitizedContent = DOMPurify.sanitize(content);
    
    // Create a div element with the pre-wrap styling
    return (
      <div 
        className="post-content" 
        style={{ whiteSpace: 'pre-wrap' }}
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      />
    );
  };

  // Debug media state changes
  useEffect(() => {
    console.log('Media viewer state:', { isMediaViewerOpen, mediaViewerIndex });
    if (isMediaViewerOpen && mediaList) {
      console.log('Current media item:', mediaList[mediaViewerIndex]);
    }
  }, [isMediaViewerOpen, mediaViewerIndex, mediaList]);

  if (isLoading) {
    return <PostSkeleton />;
  }

  return (
    <div className="post">
      <div className="post-avatar-column" onClick={handleUserClick}>
        <Avatar 
          size={40} 
          src={user.avatar} 
          account_type={user.account_type}
        />
      </div>
      <div className="post-content-column">
        <div className="post-header">
          <div className="post-user-meta" onClick={handleUserClick}>
            <div className="post-username-timestamp">
              <span className="post-username">{user.username}</span>
              <span className="post-timestamp">{formatTimestamp(timestamp)}</span>
            </div>
            {isInPostDetails && postType === 'collab' && (
              <button 
                className="post-apply-button" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (onApplyClick) {
                    onApplyClick();
                  }
                }}
                type="button"
              >
                <span>Apply</span>
                <img src={iconCollabApply} alt="Apply" className="apply-icon" />
              </button>
            )}
            {isOwnPost && postType !== 'collab' && (
              <button className="post-more-button" onClick={handleMoreClick}>
                <img src={iconMore} alt="More options" className="post-action-icon" />
              </button>
            )}
          </div>
        </div>
        <div className="post-main-content" onClick={handleContentClick}>
          {renderedContent}
          {postType === 'collab' && collab && isInPostDetails && (
            <div className="collab-buttons" onClick={e => e.stopPropagation()}>
              <button 
                className="collab-button" 
                data-type="content_mission"
                onClick={() => handleGuidelineClick('content_mission')}
              >
                <img src={imgContentMission} alt="Content Mission" />
                Content Mission
              </button>
              <button 
                className="collab-button" 
                data-type="things_to_avoid"
                onClick={() => handleGuidelineClick('things_to_avoid')}
              >
                <img src={imgThingsToAvoid} alt="Things to Avoid" />
                Things To Avoid
              </button>
              <button 
                className="collab-button" 
                data-type="products"
                onClick={() => handleGuidelineClick('products')}
              >
                <img src={imgProducts} alt="Products" />
                Products
              </button>
              <button 
                className="collab-button" 
                data-type="required_hashtags"
                onClick={() => handleGuidelineClick('required_hashtags')}
              >
                <img src={imgRequiredTags} alt="Required Tags" />
                Required Tags
              </button>
            </div>
          )}
          
          <CollabGuidelines 
            isOpen={showGuidelines}
            onClose={() => setShowGuidelines(false)}
            type={guidelineType}
            content={collab?.[guidelineType] || ''}
          />
          {mediaList && mediaList.length > 0 && (
            <div 
              className={`post-mediaList ${mediaList.length === 1 ? 'single-media' : ''}`}
              onClick={(e) => {
                // Don't trigger if clicking on a button (like Join Now)
                if ((e.target as HTMLElement).closest('button')) {
                  return;
                }
                
                console.log('Media list container clicked');
                e.stopPropagation();
                
                // Find closest media item to determine which index was clicked
                // For simplicity, we'll just use the first item for now
                const index = 0;
                setMediaViewerIndex(index);
                setIsMediaViewerOpen(true);
              }}
              style={{ cursor: 'pointer' }}
            >
              {mediaList.map((media, index) => {
                // Get image styles from the utility function
                let imageStyles = getImageStyles(media, mediaList?.length, postType);
                
                // Override width for first media in collab posts
                if (postType === 'collab' && index === 0) {
                  imageStyles = {
                    ...imageStyles,
                    width: 250
                  };
                }
                
                return (
                  <div 
                    key={index} 
                    className="post-media-container"
                    style={{
                      width: imageStyles.width || 'auto',
                      position: 'relative'
                    }}
                    // Remove click handler from individual containers
                  >
                    <VIImage 
                      src={media.url} 
                      alt={`Post media ${index + 1}`}
                      className="post-media"
                      style={{
                        ...imageStyles,
                        pointerEvents: 'none' // Disable pointer events on the image
                      }}
                    />
                    {postType === 'collab' && !isInPostDetails && index === 0 && (
                      <button 
                        className="join-now-button" 
                        onClick={handleJoinNowClick}
                        style={{ zIndex: 20 }}
                      >
                        Join Now
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="post-footer">
          <button 
            className={`post-action-button ${likedStatus ? 'liked' : ''}`} 
            onClick={handleLike}
          >
            <img 
              src={likedStatus ? iconLiked : iconLike} 
              alt="Like" 
              className="post-action-icon" 
            /> 
            {likeCount}
          </button>
          <div className="post-action-button" onClick={handleReplyClick}>
            <img 
              src={iconReply} 
              alt="Reply" 
              className="post-action-icon" 
            /> 
            <span>{numReplies}</span>
          </div>
          <button className="post-action-button" onClick={handleShare}>
            <img src={iconShare} alt="Share" className="post-action-icon" />
          </button>
        </div>
      </div>
      {showOptionsModal && isOwnPost && (
        <PostOptionModal
          onClose={handleCloseModal}
          onCopyLink={handleCopyLink}
          onDeletePost={handleDeletePost}
          postId={id}
          slug={slug}
        />
      )}
      <VIModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        title="Share posts with other Besties!"
      >
        <div className="vi-modal-url-container">
          <p className="vi-modal-url">
            {`${window.location.origin}/posts/${slug}`}
          </p>
          <button 
            className="vi-modal-copy-button"
            onClick={handleCopyUrl}
          >
            {copySuccess ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </VIModal>
      {mediaList && mediaList.length > 0 && (
        <MediaViewerModal
          isOpen={isMediaViewerOpen}
          onClose={() => setIsMediaViewerOpen(false)}
          mediaList={mediaList}
          initialIndex={mediaViewerIndex}
        />
      )}
      {showApplyModal && (
        <ApplyCollabsStepsModal
          isOpen={showApplyModal}
          onClose={() => setShowApplyModal(false)}
          onSubmit={handleApplySubmit}
          originalPost={{
            ...{
              id,
              user,
              content,
              numLikes,
              numReplies,
              timestamp,
              postType,
              mediaList,
              preview,
              likedStatus: initialLikedStatus,
              currentUser,
              currentUserId,
              onPostDeleted,
              onReplyClick,
              onClick,
              isInPostDetails,
              collab,
              isLoading,
              onApplyClick,
              slug,
              highlight_username_dict,
              collabs_application_status,
            },
            isSelfPost: false,
            isNewPost: false
          }}
          currentUser={currentUser}
        />
      )}
      <VIModal
        isOpen={showEditApplicationModal}
        onClose={() => setShowEditApplicationModal(false)}
      >
        <div className="edit-application-modal">
          <h3>You've already applied</h3>
          <p>Do you want to edit your application?</p>
          <div className="edit-application-buttons">
            <button className="secondary-button" onClick={() => setShowEditApplicationModal(false)}>No</button>
            <button className="primary-button" onClick={handleEditConfirm}>Yes, edit my application</button>
          </div>
        </div>
      </VIModal>
    </div>
  );
};

const areEqual = (prevProps: PostProps, nextProps: PostProps) => {
  return (
    prevProps.id === nextProps.id &&
    prevProps.likedStatus === nextProps.likedStatus &&
    prevProps.numLikes === nextProps.numLikes &&
    prevProps.numReplies === nextProps.numReplies &&
    prevProps.currentUserId === nextProps.currentUserId &&
    JSON.stringify(prevProps.mentions) === JSON.stringify(nextProps.mentions)
  );
};
export default memo(Post, areEqual);
